import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { AccessRole, RecordingScope, UserAccess } from '@/api/auth'

// =============================================================================
// Types
// =============================================================================

export type AgentRole = 'AGENT' | 'MANAGER'
export type AgentStatus =
  | 'ONLINE'
  | 'OFFLINE'
  | 'AWAY'
  | 'BUSY'
  | 'IN_CALL'

export interface InterfaceAgent {
  /** Photo de profil (lien signé), vide sinon. */
  avatar_url?: string
  id: number
  user_id: number
  user_email: string
  user_first_name: string
  user_last_name: string
  phone_number: string
  company: number | null
  company_name: string
  created_at: string
  status: AgentStatus
  status_display: string
  role: AgentRole
  /** Widget marque blanche : identifiant chez le partenaire (vide pour un utilisateur normal). */
  external_id?: string
  /** Nom de la clé API qui a créé l'utilisateur, vide sinon. */
  linked_via?: string
  /** Faux pour un rattaché non promu : il ne se connecte pas sur app.callizy.ca. */
  dashboard_access?: boolean
  phone_verified?: boolean
  /** Role shown in Settings > Team. */
  access_role?: AccessRole
  /** Member's own exception to the company rule ('default' = the rule). */
  recording_access?: RecordingAccessChoice
}

/** Company rule for members: their own calls, every call, or none. */
export type RecordingRule = 'own' | 'team' | 'managers'
export type RecordingAccessChoice = 'default' | RecordingScope

export interface TeamAccessState {
  rule: RecordingRule
  can_edit: boolean
  /** Only the owner names or removes a manager. */
  can_name_managers: boolean
  me: UserAccess
}

export interface MemberAccessInput {
  profileId: number
  role?: Exclude<AccessRole, 'owner'>
  recording_access?: RecordingAccessChoice
}

export interface AgentStats {
  total_redirection_agents: number
  max_redirection_agents: number
  can_add_redirection_agent: boolean
  total_interface_agents: number
  /** null = sans plafond (facturation au siège) ; un nombre sur un ancien plan à prix fixe. */
  max_interface_agents: number | null
  can_add_interface_agent: boolean
  interface_agents: InterfaceAgent[]
  is_trialing: boolean
}

export type InvitationStatus =
  | 'pending'
  | 'accepted'
  | 'cancelled'
  | 'expired'

export interface AgentInvitation {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  phone_number: string | null
  status: InvitationStatus
  created_at: string
  expires_at: string
  is_expired: boolean
}

interface InvitationsResponse {
  invitations: AgentInvitation[]
  total: number
}

export interface InviteAgentInput {
  email: string
}

export interface InviteAgentResponse {
  message: string
  invitation_id: number
  expires_at: string
}

// =============================================================================
// Queries
// =============================================================================

/** GET /api/agents/stats/ — stats + liste des agents interface. */
export function useAgentStats() {
  return useQuery({
    queryKey: ['agent-stats'],
    queryFn: async () => {
      const { data } = await api.get<AgentStats>('/api/agents/stats/')
      return data
    },
    staleTime: 15_000,
  })
}

/** GET /api/team-access/ : the company rule and what the viewer may change. */
export function useTeamAccess() {
  return useQuery({
    queryKey: ['team-access'],
    queryFn: async () => {
      const { data } = await api.get<TeamAccessState>('/api/team-access/')
      return data
    },
    staleTime: 60_000,
  })
}

/** Lists that carry a player or a transcript: they change with the access rules. */
function invalidateRecordingViews(queryClient: ReturnType<typeof useQueryClient>) {
  for (const key of ['contact-timeline', 'call-logs', 'company-call-logs', 'call-transcription', 'call-recordings', 'profile']) {
    queryClient.invalidateQueries({ queryKey: [key] })
  }
}

/** PATCH /api/team-access/ {rule} : who hears the recordings. */
export function useUpdateRecordingRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (rule: RecordingRule) => {
      const { data } = await api.patch<TeamAccessState>('/api/team-access/', { rule })
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['team-access'], data)
      queryClient.invalidateQueries({ queryKey: ['agent-stats'] })
      invalidateRecordingViews(queryClient)
    },
  })
}

/** PATCH /api/team-access/members/{id}/ : a member's role or recording exception. */
export function useUpdateMemberAccess() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ profileId, ...input }: MemberAccessInput) => {
      const { data } = await api.patch<{ id: number; access_role: AccessRole; recording_access: RecordingAccessChoice }>(
        `/api/team-access/members/${profileId}/`,
        input,
      )
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData<AgentStats>(['agent-stats'], (old) => old && {
        ...old,
        interface_agents: old.interface_agents.map((a) =>
          a.id === data.id ? { ...a, access_role: data.access_role, recording_access: data.recording_access } : a,
        ),
      })
      queryClient.invalidateQueries({ queryKey: ['agent-stats'] })
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
      invalidateRecordingViews(queryClient)
    },
  })
}

/** GET /api/agents/invitations/ — invitations en cours (pending). */
export function useAgentInvitations() {
  return useQuery({
    queryKey: ['agent-invitations'],
    queryFn: async () => {
      const { data } = await api.get<InvitationsResponse>(
        '/api/agents/invitations/',
      )
      return data.invitations ?? []
    },
    staleTime: 15_000,
  })
}

/**
 * Le backend refuse une invitation qui rendrait le compte impayé : un siège
 * est facturé dès que l'invité rejoint, et une proration refusée suspend le
 * sortant de toute l'entreprise trois jours plus tard.
 *
 * Il renvoie alors un 402 avec un code ET un message français. On traduit le
 * code quand on le connaît ; sinon on affiche le message du serveur, qui vaut
 * toujours mieux qu'un « une erreur est survenue » — c'est le seul endroit où
 * le manager apprend qu'il doit régler sa carte.
 */
const CLES_ERREUR_FACTURATION: Record<string, string> = {
  no_subscription: 'manager.team.invite.errors.noSubscription',
  payment_overdue: 'manager.team.invite.errors.paymentOverdue',
  subscription_canceled: 'manager.team.invite.errors.subscriptionCanceled',
  subscription_incomplete: 'manager.team.invite.errors.subscriptionIncomplete',
  no_payment_method: 'manager.team.invite.errors.noPaymentMethod',
}

export function messageErreurInvitation(
  data: { error?: string; message?: string; [champ: string]: unknown } | undefined,
  t: (key: string) => string,
): string {
  const cle = data?.error ? CLES_ERREUR_FACTURATION[data.error] : undefined
  if (cle) return t(cle)
  if (data?.message) return data.message
  if (typeof data?.error === 'string') return data.error
  // Les erreurs de validation DRF arrivent par champ :
  // {"email": ["Un utilisateur avec cet email existe déjà."]}.
  // Ce format était jeté, et l'agent voyait « Failed to send invitation »
  // alors que le serveur venait de dire exactement pourquoi.
  for (const valeur of Object.values(data ?? {})) {
    if (Array.isArray(valeur) && typeof valeur[0] === 'string') return valeur[0]
    if (typeof valeur === 'string' && valeur) return valeur
  }
  return t('manager.team.invite.error')
}


// =============================================================================
// Mutations
// =============================================================================

/** POST /api/agents/invite/ — invite un nouvel agent par email. */
export function useInviteAgent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: InviteAgentInput) => {
      const { data } = await api.post<InviteAgentResponse>(
        '/api/agents/invite/',
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-invitations'] })
      queryClient.invalidateQueries({ queryKey: ['agent-stats'] })
    },
  })
}

/** DELETE /api/agents/invitations/{id}/ — annule une invitation pending. */
export function useCancelInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (invitationId: number) => {
      const { data } = await api.delete<{ message: string }>(
        `/api/agents/invitations/${invitationId}/`,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-invitations'] })
    },
  })
}

/** DELETE /api/interface-agents/{id}/ — supprime un agent interface. */
export function useDeleteAgent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (agentId: number) => {
      await api.delete(`/api/interface-agents/${agentId}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-stats'] })
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
    },
  })
}

/** POST /api/integrations/widget-embarque/users/{id}/dashboard-access/ — ouvre le dashboard à un utilisateur rattaché (lien par courriel). */
export function useGrantDashboardAccess() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (profileId: number) => {
      const { data } = await api.post<{ ok: boolean; email: string }>(
        `/api/integrations/widget-embarque/users/${profileId}/dashboard-access/`,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-stats'] })
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
    },
  })
}
